import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Empty, Modal, Select, Table, Tag, Tooltip } from 'antd';
import { AccessService } from '../../service/api/access-service';
import { usePermission } from '../../core/utils/PermissionContext';
import { buildCatalogTree, collectPageRows, permissionIdsOf, CRUD_CODES } from './access-utils';

const sameSet = (a, b) => a.size === b.size && [...a].every(x => b.has(x));

const RoleMatrixTab = ({ canEdit, roleId, onRoleChange }) => {
  const { reload: reloadMyAccess } = usePermission();
  const [roles, setRoles] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [original, setOriginal] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const dirty = !sameSet(selected, original);
  const readOnly = !canEdit;

  useEffect(() => {
    AccessService.getRoles().then(setRoles).catch(() => {});
  }, []);

  const loadMatrix = useCallback(async id => {
    if (!id) {
      setMatrix(null);
      return;
    }
    setLoading(true);
    setSaveError(null);
    try {
      const result = await AccessService.getRoleMatrix(id);
      const granted = new Set(result?.grantedPermissionIds || []);
      setMatrix(result);
      setSelected(granted);
      setOriginal(new Set(granted));
      setExpandedKeys(buildCatalogTree(result?.catalog).map(m => m.key));
    } catch (_e) {
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatrix(roleId);
  }, [roleId, loadMatrix]);

  const tree = useMemo(() => buildCatalogTree(matrix?.catalog), [matrix]);

  const changeRole = id => {
    if (dirty) {
      Modal.confirm({
        title: 'Discard unsaved changes?',
        content: 'You have unsaved permission changes for this role.',
        okText: 'Discard',
        okButtonProps: { danger: true },
        onOk: () => onRoleChange(id),
      });
    } else {
      onRoleChange(id);
    }
  };

  // ── selection rules (VIEW implication, PRD §6.3) ──
  const viewOf = pageRow => pageRow.perms.find(p => p.code === 'VIEW');

  const togglePermission = (pageRow, perm, checked) => {
    const next = new Set(selected);
    if (checked) {
      next.add(perm.id);
      const view = viewOf(pageRow);
      if (perm.code !== 'VIEW' && view) next.add(view.id); // any action implies VIEW
    } else {
      next.delete(perm.id);
      if (perm.code === 'VIEW') pageRow.perms.forEach(p => next.delete(p.id)); // no actions without VIEW
    }
    setSelected(next);
  };

  const toggleNode = (node, checked) => {
    const next = new Set(selected);
    permissionIdsOf(node).forEach(id => (checked ? next.add(id) : next.delete(id)));
    setSelected(next);
  };

  const nodeState = node => {
    const ids = permissionIdsOf(node);
    const count = ids.filter(id => selected.has(id)).length;
    return { all: ids.length > 0 && count === ids.length, some: count > 0 && count < ids.length, empty: ids.length === 0 };
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await AccessService.saveRoleMatrix(matrix.role.id, [...selected], matrix.role.rowVer);
      await loadMatrix(matrix.role.id);
      reloadMyAccess();
    } catch (e) {
      // Edits are kept so nothing is lost; 409 means another admin saved first.
      setSaveError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => setSelected(new Set(original));

  const renderCrud = code => (_, row) => {
    if (row.type !== 'page') return null;
    const perm = row.perms.find(p => p.code === code);
    if (!perm) return null;
    const checked = selected.has(perm.id);
    const derived = code === 'VIEW' && checked && !original.has(perm.id) && row.perms.some(p => p.code !== 'VIEW' && selected.has(p.id));
    return (
      <>
        <Checkbox
          checked={checked}
          disabled={readOnly}
          onChange={e => togglePermission(row, perm, e.target.checked)}
          aria-label={`${row.page.name} ${perm.name}`}
        />
        {!perm.isActive && <Tag className="ms-1">inactive</Tag>}
        {derived && <span className="ac-derived">auto</span>}
      </>
    );
  };

  const columns = [
    {
      title: 'Module / Page',
      key: 'name',
      width: 300,
      render: (_, row) => {
        const item = row.type === 'module' ? row.module : row.page;
        const state = nodeState(row);
        return (
          <span className={item.isActive ? '' : 'ac-inactive'}>
            <Checkbox
              className="me-2"
              checked={state.all}
              indeterminate={state.some}
              disabled={readOnly || state.empty}
              onChange={e => toggleNode(row, e.target.checked)}
              aria-label={`Select all for ${item.name}`}
            />
            {item.name}
            {row.type === 'page' && item.routePattern && <code className="ms-2 small text-muted">{item.routePattern}</code>}
            {!item.isActive && <Tag className="ms-2">inactive</Tag>}
            {row.type === 'page' && !item.showInMenu && <Tag className="ms-2">hidden</Tag>}
          </span>
        );
      },
    },
    ...CRUD_CODES.map(code => ({
      title: code.charAt(0) + code.slice(1).toLowerCase(),
      key: code,
      width: 80,
      align: 'center',
      render: renderCrud(code),
    })),
    {
      title: 'Buttons / actions',
      key: 'buttons',
      render: (_, row) =>
        row.type !== 'page' ? null : (
          <div className="ac-buttons">
            {row.perms
              .filter(p => p.kind === 'BUTTON')
              .map(perm => (
                <Tooltip key={perm.id} title={`${perm.permissionKey} · when denied: ${perm.deniedBehavior}`}>
                  <Checkbox checked={selected.has(perm.id)} disabled={readOnly} onChange={e => togglePermission(row, perm, e.target.checked)}>
                    {perm.name}
                    {!perm.isActive && <Tag className="ms-1">inactive</Tag>}
                  </Checkbox>
                </Tooltip>
              ))}
          </div>
        ),
    },
  ];

  const role = matrix?.role;
  const grantedCount = selected.size;
  const pageCount = tree.reduce((n, m) => n + collectPageRows(m).length, 0);

  return (
    <>
      <div className="ac-toolbar">
        <Select
          showSearch
          placeholder="Select a role"
          style={{ minWidth: 300 }}
          value={roleId || undefined}
          onChange={changeRole}
          optionFilterProp="label"
          options={roles.map(r => ({ value: r.id, label: `${r.name} (${r.entraRoleValue})${r.isActive ? '' : ' – inactive'}` }))}
        />
        {role && (
          <>
            <Button onClick={() => loadMatrix(role.id)} disabled={saving}>
              Refresh
            </Button>
            <Button onClick={() => setExpandedKeys(tree.map(m => m.key))}>Expand all</Button>
            <Button onClick={() => setExpandedKeys([])}>Collapse all</Button>
          </>
        )}
        <span className="ac-spacer" />
        {role && !readOnly && (
          <>
            {dirty && <span className="ac-dirty">Unsaved changes</span>}
            <Button onClick={discard} disabled={!dirty || saving}>
              Discard
            </Button>
            <Button type="primary" onClick={save} disabled={!dirty} loading={saving}>
              Save
            </Button>
          </>
        )}
      </div>

      {!roleId && <Empty description="Select a role to view or edit its permissions" />}

      {role && (
        <>
          <div className="mb-2 small text-muted">
            <code>{role.entraRoleValue}</code> · {role.isActive ? 'Active' : 'Inactive'} · {grantedCount} permissions selected across {pageCount} pages
            {readOnly && ' · read-only (you need Permission Matrix Edit)'}
          </div>
          {role.isSuperAdmin && (
            <Alert className="mb-2" type="warning" showIcon message="This role is super-admin: it bypasses every application permission. Grants below apply only if super-admin is turned off." />
          )}
          {!role.isActive && <Alert className="mb-2" type="info" showIcon message="This role is inactive, so its grants have no effect until it is activated." />}
          {saveError && (
            <Alert
              className="mb-2"
              type="error"
              showIcon
              message={saveError}
              description="Your edits are kept. If another administrator saved this role first, use Refresh to load their version (your edits will be discarded)."
            />
          )}
          <Table
            rowKey="key"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={tree}
            pagination={false}
            rowClassName={row => (row.type === 'module' ? 'ac-module-row' : '')}
            expandable={{ expandedRowKeys: expandedKeys, onExpandedRowsChange: keys => setExpandedKeys(keys) }}
            scroll={{ x: 1000 }}
          />
        </>
      )}
    </>
  );
};

export default RoleMatrixTab;
